/**
 * Prototype-only: the catalogue and the revenue statements the failing import
 * lands next to. "Good Boy" is the release the revenue calculation trips over —
 * it exists, it just has no tracks to allocate revenue to.
 */
import type { RevenueStatement } from "@/types";

/** The sentence the backend stores on the statement, shown and never parsed. */
export const REVENUE_ERROR =
  "Release 'Good Boy' has revenue but no tracks to allocate it to (row 5248). Add its tracks to the catalog and re-import.";

export const BROKEN_RELEASE_TITLE = "Good Boy";

const LABEL_ID = "dev-label";

export function makeStatement(overrides: Partial<RevenueStatement> = {}): RevenueStatement {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    file_name: "spotify_q1_2026.csv",
    import_batch: "batch-seed",
    label: LABEL_ID,
    uploaded_by: "dev@local.test",
    vendor: "spotify",
    currency: "USD",
    period_start: "2026-01-01",
    period_end: "2026-03-31",
    statement_received_date: "2026-03-31",
    file: "/spotify_q1_2026.csv",
    status: "processed",
    total_lines: 5248,
    revenue: "12480.55",
    created_at: now,
    updated_at: now,
    name: null,
    can_delete: true,
    amount_received: null,
    exchange_rate: null,
    spot_rate: null,
    spot_rate_date: null,
    date_received: null,
    is_settled: false,
    is_settling: false,
    settlement_error: "",
    import_error: "",
    settlement_difference_reason: null,
    settlement_quantity_difference: null,
    settlement_fx_difference: null,
    expected_amount: "12480.55",
    expected_foreign_amount: null,
    ...overrides,
  };
}

/** Two statements that already went through, so the list is not one row. */
export const SEEDED_STATEMENTS: RevenueStatement[] = [
  makeStatement({
    id: "stmt-seed-1",
    file_name: "spotify_q4_2025.csv",
    period_start: "2025-10-01",
    period_end: "2025-12-31",
    statement_received_date: "2025-12-31",
    revenue: "9814.20",
    expected_amount: "9814.20",
    amount_received: "9814.20",
    date_received: "2026-01-18",
    is_settled: true,
  }),
  makeStatement({
    id: "stmt-seed-2",
    file_name: "believe_q4_2025.csv",
    vendor: "believe",
    currency: "EUR",
    period_start: "2025-10-01",
    period_end: "2025-12-31",
    statement_received_date: "2025-12-31",
    revenue: "4210.00",
    expected_amount: "4552.31",
    expected_foreign_amount: "4210.00",
  }),
];

/** Rows for the details tabs of a statement that imported successfully. */
export const STATEMENT_TRACKS = [
  {
    id: "track-1",
    title: "Night Ferry",
    name: "Night Ferry",
    isrc: "GBKQU2100142",
    release: "Night Ferry",
    release_title: "Night Ferry",
    quantity: 18422,
    total_revenue: "412.88",
    totalRevenue: 412.88,
    revenue: "412.88",
    contributors: ["Halla Vey"],
  },
  {
    id: "track-2",
    title: "Harbour Lights",
    name: "Harbour Lights",
    isrc: "GBKQU2100143",
    release: "Night Ferry",
    release_title: "Night Ferry",
    quantity: 9310,
    total_revenue: "208.44",
    totalRevenue: 208.44,
    revenue: "208.44",
    contributors: ["Halla Vey"],
  },
  {
    id: "track-3",
    title: "Paper Anchor",
    name: "Paper Anchor",
    isrc: "GBKQU2100177",
    release: "Paper Anchor",
    release_title: "Paper Anchor",
    quantity: 44105,
    total_revenue: "987.10",
    totalRevenue: 987.1,
    revenue: "987.10",
    contributors: ["Halla Vey", "Juno Fields"],
  },
];

export const STATEMENT_CONTRIBUTORS = [
  {
    id: "contributor-1",
    name: "Halla Vey",
    display_name: "Halla Vey",
    quantity: 71837,
    total_revenue: "1608.42",
    revenue: "1608.42",
  },
  {
    id: "contributor-2",
    name: "Juno Fields",
    display_name: "Juno Fields",
    quantity: 44105,
    total_revenue: "987.10",
    revenue: "987.10",
  },
];

interface CatalogRelease {
  id: string;
  title: string;
  type: string;
  label: string;
  contributors: { id: string; display_name: string; country: string }[];
  created_at: string;
  updated_at: string;
  track_count: number;
  release_date: string;
  catalog_number: string;
  upc_code: string;
  territory: string;
  status: string;
  can_delete: boolean;
}

function makeRelease(overrides: Partial<CatalogRelease>): CatalogRelease {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: "Untitled",
    type: "single",
    label: LABEL_ID,
    contributors: [{ id: "con-1", display_name: "Halla Vey", country: "GB" }],
    created_at: now,
    updated_at: now,
    track_count: 1,
    release_date: "2026-01-16",
    catalog_number: "TRK-0142",
    upc_code: "5060204802256",
    territory: "Worldwide",
    status: "active",
    can_delete: true,
    ...overrides,
  };
}

/**
 * The catalogue behind the flow. "Good Boy" carries no tracks until the dev
 * bar says the catalogue has been fixed — the same dial the re-import reads.
 */
export function catalogReleases(catalogFixed: boolean): CatalogRelease[] {
  return [
    makeRelease({
      id: "rel-good-boy",
      title: BROKEN_RELEASE_TITLE,
      type: "single",
      catalog_number: "TRK-0163",
      upc_code: "5060204802287",
      release_date: "2026-02-06",
      contributors: [{ id: "con-3", display_name: "Marn Ottoline", country: "GB" }],
      track_count: catalogFixed ? 2 : 0,
    }),
    makeRelease({
      id: "rel-night-ferry",
      title: "Night Ferry",
      type: "ep",
      track_count: 2,
    }),
    makeRelease({
      id: "rel-paper-anchor",
      title: "Paper Anchor",
      type: "single",
      catalog_number: "TRK-0150",
      upc_code: "5060204802263",
      track_count: 1,
    }),
  ];
}

export function catalogRecordings(catalogFixed: boolean) {
  const base = [
    {
      id: "rec-1",
      title: "Night Ferry",
      isrc: "GBKQU2100142",
      release_details: { id: "rel-night-ferry", title: "Night Ferry" },
      contributors: [{ id: "con-1", display_name: "Halla Vey", country: "GB" }],
      track_number: "1",
      duration_seconds: 214,
    },
    {
      id: "rec-2",
      title: "Harbour Lights",
      isrc: "GBKQU2100143",
      release_details: { id: "rel-night-ferry", title: "Night Ferry" },
      contributors: [{ id: "con-1", display_name: "Halla Vey", country: "GB" }],
      track_number: "2",
      duration_seconds: 187,
    },
  ];
  if (!catalogFixed) return base;
  return [
    ...base,
    {
      id: "rec-good-boy-1",
      title: "Good Boy",
      isrc: "GBKQU2100201",
      release_details: { id: "rel-good-boy", title: BROKEN_RELEASE_TITLE },
      contributors: [{ id: "con-3", display_name: "Marn Ottoline", country: "GB" }],
      track_number: "1",
      duration_seconds: 199,
    },
    {
      id: "rec-good-boy-2",
      title: "Good Boy (Reprise)",
      isrc: "GBKQU2100202",
      release_details: { id: "rel-good-boy", title: BROKEN_RELEASE_TITLE },
      contributors: [{ id: "con-3", display_name: "Marn Ottoline", country: "GB" }],
      track_number: "2",
      duration_seconds: 142,
    },
  ];
}
